import { loadProfile } from "../stats/profile";
import { DECK_UNLOCKS, evaluateUnlock, isOffered, type UnlockState } from "./unlocks";

/**
 * `crystals` and `blossoms` are the Epic decks' (theme/rarity.ts): ornate
 * patterns that dress the back, the frame and the wilds as well as the face's
 * watermark. See `isOrnate` in pixi/cardArt.ts.
 */
export type CardPattern = "none" | "dots" | "stripes" | "diamonds" | "waves" | "crystals" | "blossoms";

export interface CardTheme {
  id: string;
  name: string;
  /** Slightly desaturated primary color, used for most of the card face. */
  primary: string;
  /** Bold secondary/accent color, used sparingly (value text, borders, wild cards). */
  secondary: string;
  pattern: CardPattern;
  /**
   * Premium decks only: the id of a painted art deck under
   * `apps/web/public/decks/<id>/`, loaded by `theme/artDecks.ts`.
   *
   * `primary`/`secondary`/`pattern` are still required and still used, for two
   * reasons that are both about the deck never being the thing that breaks: the
   * app dresses menus and UI accents from them, and a card falls back to the
   * fully procedural face if the art has not loaded. A premium deck that fails
   * to download is a plain card, not a missing one.
   */
  art?: string;
}

/**
 * The card decks. Two are available from the start; the rest unlock through
 * levels and specific achievements (see theme/unlocks.ts).
 *
 * The custom theme builder has been removed — it competed with the unlock
 * economy it now sits alongside. Anything saved by the old builder is cleaned
 * up on first load (early alpha, so the churn is accepted), and
 * `getSelectedTheme()` falls back to a starter if the stored selection no longer
 * resolves or is no longer unlocked.
 *
 * Solo high scores used to live in this file as a single localStorage key per
 * deck size; they now live in the player profile as proper top-10 boards
 * (src/stats/profile.ts).
 */
export const CARD_THEMES: CardTheme[] = [
  { id: "classic-slate", name: "Classic Slate", primary: "#5b6b7c", secondary: "#ef8354", pattern: "dots" },
  { id: "forest", name: "Forest", primary: "#4f6f52", secondary: "#f2c14e", pattern: "waves" },
  { id: "harbor", name: "Harbor", primary: "#3f6683", secondary: "#f76c5e", pattern: "stripes" },
  { id: "plum", name: "Plum", primary: "#6b5876", secondary: "#ffd166", pattern: "diamonds" },
  { id: "clay", name: "Clay", primary: "#8a6f5c", secondary: "#2ec4b6", pattern: "dots" },
  { id: "ember", name: "Ember", primary: "#7d4438", secondary: "#ffb03a", pattern: "diamonds" },
  { id: "frost", name: "Frost", primary: "#586d84", secondary: "#8fe3e8", pattern: "crystals" },
  { id: "orchid", name: "Orchid", primary: "#6a4a72", secondary: "#f3a8c8", pattern: "blossoms" },
  // Premium decks, painted rather than drawn (theme/artDecks.ts). Generated from
  // the specs in assets/decks by `node scripts/deckgen.mjs install`, so that
  // building a deck is the same act as making it selectable.
  // >>> generated by scripts/deckgen.mjs - do not edit by hand
  {
    id: "animal-antics-deck",
    name: "Animal Antics",
    primary: "#092328",
    secondary: "#3dbad0",
    pattern: "none",
    art: "animal-antics-deck",
  },
  {
    id: "biolume-deep",
    name: "Biolume Deep",
    primary: "#211809",
    secondary: "#FFBF00",
    pattern: "none",
    art: "biolume-deep",
  },
  {
    id: "fractal-aether",
    name: "Fractal Aether",
    primary: "#291b26",
    secondary: "#ae73a1",
    pattern: "none",
    art: "fractal-aether",
  },
  {
    id: "midnight-macabre",
    name: "Midnight Macabre",
    primary: "#2c1b17",
    secondary: "#ab6455",
    pattern: "none",
    art: "midnight-macabre",
  },
  {
    id: "mythic-courts",
    name: "Mythic Courts",
    primary: "#16151a",
    secondary: "#f0a63c",
    pattern: "none",
    art: "mythic-courts",
  },
  {
    id: "norse-realms",
    name: "Norse Realms",
    primary: "#1a1f2d",
    secondary: "#6c86c0",
    pattern: "none",
    art: "norse-realms",
  },
  {
    id: "olympian-pantheon",
    name: "Olympian Pantheon",
    primary: "#1c1f2d",
    secondary: "#7384bf",
    pattern: "none",
    art: "olympian-pantheon",
  },
  {
    id: "world-in-bloom",
    name: "World in Bloom",
    primary: "#1c1229",
    secondary: "#8a5cc9",
    pattern: "none",
    art: "world-in-bloom",
  },
  {
    id: "yuletide-joy",
    name: "Yuletide Joy",
    primary: "#11231c",
    secondary: "#328566",
    pattern: "none",
    art: "yuletide-joy",
  },
  // <<< end generated
];

/**
 * Built and registered, but not on any shelf yet: left out of `themesWithUnlocks`
 * and never selectable, host comp included, until it has unlock conditions of
 * its own. Kept out here rather than by editing the generated blocks, which
 * `deckgen` would write straight back. A table still resolves it through
 * `themeById`, so nothing breaks if a save somehow names it.
 */
const UNRELEASED_THEME_IDS: ReadonlySet<string> = new Set();

/** Kept only so the one-time cleanup below can find themes from the removed builder. */
const LEGACY_CUSTOM_THEMES_KEY = "fiveout:customThemes";
const SELECTED_THEME_KEY = "fiveout:selectedThemeId";

const STARTER_THEME_ID = CARD_THEMES[0].id;

/** Drops anything left over from the removed custom theme builder. Called once at boot. */
export function cleanUpLegacyThemes(): void {
  try {
    localStorage.removeItem(LEGACY_CUSTOM_THEMES_KEY);
  } catch {
    /* nothing to clean up */
  }
}

export function allThemes(): CardTheme[] {
  return CARD_THEMES;
}

export function themeById(id: string): CardTheme | undefined {
  return CARD_THEMES.find((t) => t.id === id);
}

export interface ThemeWithUnlock extends CardTheme {
  unlock: UnlockState;
}

/**
 * Every deck on offer with its current unlock state, in catalog order.
 *
 * The one deck ever left out is a seasonal prize whose window is closed, for a
 * player who never took part (`isOffered`) — there is nothing they can do to get
 * it today. Anyone can still play WITH it at a table whose host holds it: the
 * table resolves its deck through `themeById`, which is the whole catalog.
 *
 * A premium deck the player cannot have is shown LOCKED, reading "Coming soon",
 * and previews its real painted art like any other — `renderCardPreview` loads
 * the deck's images whatever the player has unlocked, because they are public
 * static files. Nobody buys a deck they were never shown, and the same is true
 * of wanting one.
 *
 * It used to be left out entirely, on the argument that "Premium deck" is not an
 * invitation the way "Reach level 4" is, and a row naming a price with no way to
 * pay it is a dead end. That reasoning was about the LABEL, and the label is now
 * honest about being a wait rather than a task — so the deck can be on the shelf
 * while the till is still being built.
 *
 * When billing lands (PRODUCTION_LAUNCH_PLAN.md §B2) the change is in
 * `describeRule`, not here: "Coming soon" becomes a price and a Buy button.
 */
export function themesWithUnlocks(nowMs: number = Date.now()): ThemeWithUnlock[] {
  const profile = loadProfile();
  return CARD_THEMES.filter((t) => !UNRELEASED_THEME_IDS.has(t.id) && isOffered(DECK_UNLOCKS[t.id], profile, nowMs)).map((t) => ({
    ...t,
    unlock: evaluateUnlock(DECK_UNLOCKS[t.id] ?? { kind: "starter" }, profile),
  }));
}

export function isThemeUnlocked(id: string): boolean {
  if (UNRELEASED_THEME_IDS.has(id)) return false;
  return evaluateUnlock(DECK_UNLOCKS[id] ?? { kind: "starter" }, loadProfile()).unlocked;
}

export function getSelectedThemeId(): string {
  const stored = localStorage.getItem(SELECTED_THEME_KEY);
  if (stored && themeById(stored) && isThemeUnlocked(stored)) return stored;
  return STARTER_THEME_ID;
}

export function setSelectedThemeId(id: string): void {
  if (!themeById(id) || !isThemeUnlocked(id)) return;
  localStorage.setItem(SELECTED_THEME_KEY, id);
  // Start rendering the new deck's cards for the screen transitions now, rather
  // than when the player navigates away from the picker — which is the same
  // instant the transition needs them. Lazy, so this module keeps its own weight
  // and drags nothing in at load; `applyVenue` reaches for the soundscape the
  // same way and for the same reason.
  void import("../ui/transitions/cardImages").then((m) => m.primeCardImages());
}

export function getSelectedTheme(): CardTheme {
  return themeById(getSelectedThemeId()) ?? CARD_THEMES[0];
}
